import React from 'react';
import ReactDOMServer from 'react-dom/server';

import { ApexOptions } from 'apexcharts';

import { DownloadCloudIcon } from '@icons';

import { ReportChart } from '@app/interfaces/report.type';

const DEFAULT_BORDER_RADIUS = 5;

const DEFAULT_TRANSLATION_LIST = {
  download: 'Скачать SVG',
  selection: 'Выбрать',
  selectionZoom: 'Увеличить выбранное',
  zoomIn: 'Увеличить',
  zoomOut: 'Уменьшить',
  pan: 'Переместить',
  reset: 'Сбросить настройки',
};

const PIE_CHART_TYPES: ReportChart['type'][] = ['piechart', 'donut', 'radialBars'];
const LINE_CHART_TYPES: ReportChart['type'][] = ['linechart', 'areaCharts'];

const TRANSPARENT_COLOR = 'rgba(255,0,0,0.0)';

const roundClosestLegendNumber = (value: number): number => {
  const multiplier = 10 ** Math.floor(Math.log10(value));

  return Math.ceil(value / multiplier) * multiplier;
};

const downloadChartMenuCache = new Map<string, string>();

const downloadChartMenu = (title?: string) => {
  const cacheKey = title || '';
  const cachedMarkup = downloadChartMenuCache.get(cacheKey);

  if (cachedMarkup) {
    return cachedMarkup;
  }

  const markup = ReactDOMServer.renderToString(
    <div title="" data-title={title} className="mt-[2px] ml-[3px] customTitleStyles">
      <DownloadCloudIcon
        className="text-3color active:scale-[90%] z-[0] hover:opacity-80"
        size={22}
      />
    </div>,
  );

  downloadChartMenuCache.set(cacheKey, markup);

  return markup;
};

const isPieChart = (chartType?: ReportChart['type'], withoutRadial = false) => {
  return (withoutRadial ? PIE_CHART_TYPES.slice(0, 2) : PIE_CHART_TYPES).includes(
    chartType as ReportChart['type'],
  );
};
const seriesCalculator = (chart: ReportChart) => {
  if (!chart?.series?.length) {
    return [];
  }

  const maxPoints = Number(chart.custom_properties?.chart_categories_length);

  const normalizeData = (data?: any[]) => {
    if (!Array.isArray(data)) return [];

    const safeData = data.map((v) => {
      if (typeof v === 'number') return v;

      if (typeof v === 'string') {
        const parsed = parseFloat(v.replace('%', ''));
        return Number.isFinite(parsed) ? parsed : 0;
      }

      return 0;
    });

    return Number.isFinite(maxPoints) ? safeData.slice(0, maxPoints) : safeData;
  };

  if (chart.type === 'piechart' || chart.type === 'donut' || chart.type === 'radialBars') {
    return normalizeData(chart.series[0]?.data);
  }

  const baseSeries = chart.series.map((seriesItem) => ({
    name: seriesItem.name,
    data: normalizeData(seriesItem.data),
  }));

  if (chart.type === 'radialCharts' && chart.line_settings?.active) {
    const values = chart.line_settings.value || [];

    const length = baseSeries[0]?.data.length || 0;

    if (values.length < 2) {
      return baseSeries;
    }

    const isLine = values[0] === values[1];

    const normalizeLineValue = (v: any) => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string') {
        const parsed = parseFloat(v.replace('%', ''));
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    };

    const v0 = normalizeLineValue(values[0]);
    const v1 = normalizeLineValue(values[1]);

    return [
      ...baseSeries,
      {
        name: isLine ? 'Норма' : 'Мин норма',
        data: Array(length).fill(v0),
      },
      ...(isLine
        ? []
        : [
            {
              name: 'Макс норма',
              data: Array(length).fill(v1),
            },
          ]),
    ];
  }

  return baseSeries;
};
const chartLabelTypeByChartType = (chart: ReportChart) => {
  const maxPoints = Number(chart.custom_properties?.chart_categories_length);

  const limit = (arr: string[] = []) =>
    Number.isFinite(maxPoints) ? arr.slice(0, maxPoints) : arr;

  if (isPieChart(chart.type)) {
    return limit(chart.categories);
  }

  if (chart.type === 'radialBars') {
    return limit(chart.categories);
  }

  return chart.series?.map((seriesItem) => seriesItem.name) || [];
};
const chartColorByChartType = (chart: ReportChart) => {
  const metricReverseColors = chart.group?.color || [];

  const isMetricReverse = chart.group?.type === 'metric_reverse' && metricReverseColors.length > 0;

  const getCycleColor = (index: number) => metricReverseColors[index % metricReverseColors.length];

  if (isPieChart(chart.type, true)) {
    if (isMetricReverse) {
      return chart.series?.[0]?.data?.map((_, index) => getCycleColor(index));
    }

    return [...(chart.series?.[0]?.color || [])];
  }

  const result =
    chart.series?.map((seriesItem, index) => {
      const fallbackColor = isMetricReverse
        ? getCycleColor(index)
        : seriesItem.color?.[0] || '#000';

      if (seriesItem.conditional_formatting?.length) {
        return ({ value }: { value: number }) => {
          const conditionalColor = seriesItem.conditional_formatting?.find(
            (item) => value >= item.value[0] && value <= item.value[1],
          )?.color;

          return conditionalColor || fallbackColor;
        };
      }

      return fallbackColor;
    }) || [];

  if (chart.type === 'radialCharts' && chart.line_settings?.active) {
    const isLine = chart.line_settings.value?.[0] === chart.line_settings.value?.[1];

    return isLine
      ? [...result, chart.line_settings.color]
      : [...result, TRANSPARENT_COLOR, TRANSPARENT_COLOR];
  }

  return result;
};

const chartAnnotationByChartType = (chart: ReportChart) => {
  if (!chart.line_settings?.active || chart.type === 'radialCharts') {
    return {};
  }

  const isLine = chart.line_settings.value?.[0] === chart.line_settings.value?.[1];

  const commonAnnotationSettings = {
    borderColor: chart.line_settings.color,
    strokeDashArray: isLine ? 7 : 0,
    opacity: isLine ? 0.5 : 0.07,
    borderWidth: isLine ? 2 : 1,
  };

  if (chart.type === 'horizontal_barchart') {
    return {
      xaxis: [
        {
          x: chart.line_settings.value[0],
          x2: isLine ? undefined : chart.line_settings.value?.[1],
          ...commonAnnotationSettings,
        },
      ],
    };
  }

  return {
    yaxis: [
      {
        y: chart.line_settings.value[0],
        y2: isLine ? undefined : chart.line_settings.value?.[1],
        fillColor: chart.line_settings.color,
        offsetX: -1000,
        width: '100000%',
        ...commonAnnotationSettings,
      },
    ],
  };
};

const themeForPieChart = (chart: ReportChart) => {
  if (!isPieChart(chart.type)) {
    return {};
  }

  return {
    theme: {
      palette: 'palette1',
    },
  };
};

const getChartFill = (chart: ReportChart, series = seriesCalculator(chart)) => {
  if (chart.type !== 'radialCharts') {
    return {};
  }

  return {
    opacity: series.map((seriesItem) => {
      const name = seriesItem.name?.toLowerCase() || '';

      if (name.includes('макс')) return 0.15;
      if (name.includes('мин')) return 1;
      if (name.includes('норма')) return 0;

      return 0.2;
    }),
  };
};

const getChartMarkers = (chart: ReportChart, series = seriesCalculator(chart)) => {
  if (chart.type !== 'radialCharts') {
    return {};
  }

  return {
    size: series.map((seriesItem) => (seriesItem.name?.toLowerCase().includes('норма') ? 0 : 3)),
  };
};

const getStrokeSettingsByChartType = (chart: ReportChart, series = seriesCalculator(chart)) => {
  if (chart.type === 'radialCharts') {
    const metricReverseColors = chart.group?.color || [];
    const isMetricReverse =
      chart.group?.type === 'metric_reverse' && metricReverseColors.length > 0;
    const getCycleColor = (index: number) =>
      metricReverseColors[index % metricReverseColors.length];
    return {
      width: 2,
      colors: series.map((seriesItem, index) =>
        seriesItem.name?.toLowerCase().includes('норма')
          ? chart.line_settings?.color
          : !isMetricReverse
          ? chart.series[index]?.color?.[0]
          : getCycleColor(index),
      ),
      dashArray: series.map((seriesItem) =>
        seriesItem.name?.toLowerCase().includes('норма') ? 6 : 0,
      ),
    };
  }

  if (!LINE_CHART_TYPES.includes(chart.type as ReportChart['type'])) {
    return {
      colors: ['transparent'],
      width: 4,
    };
  }

  return {};
};

const selectChartType = (type?: ReportChart['type']) => {
  const DEFAULT_CHART: ReportChart['type'] = 'barchart';

  const chartSettings: Record<
    ReportChart['type'],
    {
      chart: ApexOptions['chart'];
      plotOptions?: ApexOptions['plotOptions'];
    }
  > = {
    piechart: {
      chart: { type: 'pie' },
    },

    linechart: {
      chart: { type: 'line' },
    },

    number: {
      chart: { type: 'line' },
    },

    columnchart: {
      chart: { type: 'bar', stacked: true },
    },

    horizontal_barchart: {
      chart: { type: 'bar' },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
    },

    barchart: {
      chart: { type: 'bar' },
      plotOptions: {
        bar: {
          horizontal: false,
        },
      },
    },

    areaCharts: {
      chart: { type: 'area' },
    },

    donut: {
      chart: { type: 'donut' },
    },

    heatMap: {
      chart: { type: 'heatmap' },
    },

    radialBars: {
      chart: { type: 'radialBar' },
    },

    radialCharts: {
      chart: { type: 'radar' },
    },
  };

  return chartSettings[type || DEFAULT_CHART] || chartSettings[DEFAULT_CHART];
};

const xasisHideObj = {
  show: false,
  labels: {
    show: false,
  },
  axisBorder: {
    show: false,
  },
  axisTicks: {
    show: false,
  },
  tooltip: {
    enabled: false,
  },
  crosshairs: {
    show: false,
  },
};

const yaxisHideObj = {
  show: false,
  labels: {
    show: false,
  },
  axisBorder: {
    show: false,
  },
  axisTicks: {
    show: false,
  },
  tooltip: {
    enabled: false,
  },
};
export const chartBuildData = ({
  chart,
  translationList,
  events,
  showToolbar,
  chartWidth,
  noDataText,
  tooltipDynamicOffset,
  tooltipCustomOffset,
  widgetMode,
}: {
  chart: ReportChart;
  translationList?: Partial<typeof DEFAULT_TRANSLATION_LIST>;
  isDashboard?: boolean;
  events?: any;
  showToolbar?: boolean;
  widgetMode?: boolean;
  chartWidth?: number;
  tooltipDynamicOffset?: number;
  tooltipCustomOffset?: number;
  noDataText?: { text: string };
}) => {
  if (chart.type === 'number') {
    return;
  }

  const calculatedSeries = seriesCalculator(chart);

  const chartLabels = chartLabelTypeByChartType(chart);

  const chartColors = chartColorByChartType(chart);

  const chartStroke = getStrokeSettingsByChartType(chart, calculatedSeries);

  const chartMarkers = getChartMarkers(chart, calculatedSeries);

  const chartFill = getChartFill(chart, calculatedSeries);

  const chartTypeSettings = selectChartType(chart.type);

  const chartTheme = themeForPieChart(chart);

  const chartAnnotations = chartAnnotationByChartType(chart);

  const downloadMarkup = downloadChartMenu((translationList || DEFAULT_TRANSLATION_LIST).download);

  return {
    options: {
      tooltip: {
        enabled: true,

        y: {
          formatter(value: number) {
            return String(value);
          },
        },
      },

      labels: chartLabels,

      grid: {
        show:
          chart.custom_properties?.grid_vertical_show ||
          chart.custom_properties?.grid_horizontal_show,
        strokeDashArray: 3.3,
        position: 'back' as const,
        borderColor: '#E5E8EB',

        xaxis: {
          lines: {
            show: chart.custom_properties?.grid_vertical_show,
          },
        },

        yaxis: {
          lines: {
            show: chart.custom_properties?.grid_horizontal_show,
          },
        },
      },

      stroke: isPieChart(chart.type)
        ? {
            show: false,
            width: 0,
          }
        : {
            ...chartStroke,
            curve: 'smooth' as const,
          },

      markers: {
        ...chartMarkers,
      },

      fill: {
        ...chartFill,
      },

      yaxis:
        chart.custom_properties?.y_axis_show !== false
          ? {
              forceNiceScale: typeof chart.max_value_limit !== 'number',
              decimalsInFloat: 0,
              min: chart.yaxis_min_value ? 0 : 0,

              max(max: number) {
                if (typeof chart.max_value_limit === 'number') {
                  return chart.max_value_limit;
                }

                const percents = Math.round(max) * 0.12;

                return roundClosestLegendNumber(max + percents);
              },
            }
          : yaxisHideObj,

      ...chartTheme,

      chart: {
        redrawOnParentResize: true,
        id: `chart_${chart.graph_id}-${chart.type}`,
        ...chartTypeSettings.chart,

        offsetX: 10,

        animations: {
          enabled: false,
        },

        events: events || {},

        toolbar: {
          show: chartWidth ? chartWidth > 260 : true,

          offsetX: tooltipDynamicOffset,

          offsetY: tooltipCustomOffset ?? -49,

          tools: {
            download: widgetMode ? false : downloadMarkup,
            pan: showToolbar,
            zoom: showToolbar,
            selection: showToolbar,
            zoomin: showToolbar,
            zoomout: showToolbar,
            reset: showToolbar,
          },
        },

        defaultLocale: 'ru',

        locales: [
          {
            name: 'ru',

            options: {
              toolbar: {
                ...DEFAULT_TRANSLATION_LIST,
                ...translationList,
              },
            },
          },
        ],
      },

      dropShadow: {
        enabled: false,
      },

      dataLabels: {
        enabled: chart.display_data,

        formatter(val: number | string, config: any) {
          const aggregate = isPieChart(chart.type)
            ? chart.series?.[0]?.aggregate
            : chart.series?.[config?.seriesIndex]?.aggregate;

          if (isPieChart(chart.type)) {
            return `${Math.ceil(Number(val))}%`;
          }

          if (aggregate === 'prop_num') {
            return `${val}%`;
          }

          if (!chart.nullable_values) {
            return val !== 0 ? val : '';
          }

          return String(val);
        },

        offsetY:
          chart.styles?.value_position === 'inside'
            ? 0
            : chart.type === 'barchart' || chart.type === 'columnchart'
            ? -15
            : chart.type === 'horizontal_barchart'
            ? -2
            : 0,

        offsetX:
          chart.type === 'horizontal_barchart'
            ? chart.styles?.value_position === 'inside'
              ? -15
              : Math.max(
                  ...chart.series.map((s) => Math.max(...s.data.map((d) => String(d).length))),
                ) * 9
            : 0,

        style: {
          fontSize: `${chart.styles?.text_size || 12}px`,

          colors: chart.styles?.text_color ? [chart.styles.text_color] : ['rgb(114, 114, 114)'],
        },

        dropShadow: {
          enabled: false,
        },
      },

      colors: chartColors,

      plotOptions: {
        bar: {
          borderRadiusApplication: 'end' as const,

          borderRadius: DEFAULT_BORDER_RADIUS,

          dataLabels: {
            position: 'top',
          },

          horizontal: false,

          ...(chartTypeSettings.plotOptions?.bar || {}),
        },

        pie: {
          donut: {
            size: chart.display_data ? '60%' : '85%',
          },
        },
        radialBar: {
          dataLabels: {
            value: {
              formatter: (val) => {
                // const aggregate = chart.series?.[0]?.aggregate;
                // if (aggregate === 'prop_num') {
                //   return `${val}%`;
                // }
                return val;
              },
            },
          },
        },
      },

      xaxis:
        chart.type === 'donut' || chart.type === 'piechart' || chart.type === 'radialBars'
          ? {}
          : chart.custom_properties?.x_axis_show !== false
          ? {
              categories: Number.isFinite(Number(chart.custom_properties?.chart_categories_length))
                ? [...(chart.categories || [])].slice(
                    0,
                    Number(chart.custom_properties?.chart_categories_length),
                  )
                : chart.categories || [],
              tickPlacement: 'between',
              max: chart.max_group_display || undefined,
              labels: {
                rotate: -45,
                trim: chart.type !== 'horizontal_barchart',
                hideOverlappingLabels: false,
              },
            }
          : {
              ...xasisHideObj,
              categories: chart.categories || [],
            },

      noData: noDataText,

      legend: {
        position: (chartWidth || 0) > 500 ? chart.styles?.legend_position : 'bottom',

        horizontalAlign: 'center' as const,

        show: chart.display_legend,

        showForSingleSeries: chart.display_legend,

        showForNullSeries: chart.display_legend,

        showForZeroSeries: chart.display_legend,
      },

      annotations: chartAnnotations,
    },

    series: calculatedSeries,
  };
};
